import { SelectOptions } from 'components/commonType';
import { FireableUpdateEventListener, UpdateEventListener } from 'utils/UpdateEventListener';
import { Actor, PartnershipMode } from 'core';
import { SessionStorageHelper, SessionStorageItemKeys } from 'helper/StorageHelper';
import { AdvertiserListModel, DefaultAdvertiserListModel } from '../AdvertiserList/AdvertiserListModel';
import { isSystemAdmin } from 'helper/ActorHelper';
import { createSelectOptionsFromEnum } from 'utils/SelectOptionsUtils';

export interface AdvertiserHomeModel {
  readonly agencyId?: number;
  readonly event: UpdateEventListener<AdvertiserHomeModel>;
  readonly state: AdvertiserHomeState;
  readonly agencyOptions: SelectOptions[];
  readonly partnershipModeOptions: SelectOptions[];
  readonly isSysAdmin: boolean;
  setAgencyId (agencyId?: number): void;
  setPartnershipModeId (partnershipModeId?: PartnershipMode): void;
  setAgencyOptions (agencyOptions: SelectOptions[]): void;
  onUnmount (eventHandler?: number): void;
  getAdvertiserListModel (): AdvertiserListModel;
}

export type AdvertiserHomeProps = {
  readonly model: AdvertiserHomeModel
};

export type AdvertiserHomeState = {
  readonly agencyId?: number;
  readonly partnershipModeId?: PartnershipMode;
};

export class DefaultAdvertiserHomeModel implements AdvertiserHomeModel {

  agencyId?: number;
  partnershipModeId?: PartnershipMode;
  isSysAdmin: boolean;
  event: FireableUpdateEventListener<AdvertiserHomeModel>;
  advertiserListModel?: AdvertiserListModel;
  partnershipModeOptions: SelectOptions[];

  constructor (
    actor: Actor,
    public agencyOptions: SelectOptions[]
  ) {
    this.isSysAdmin = isSystemAdmin(actor);
    this.event = new FireableUpdateEventListener<AdvertiserHomeModel>();
    this.agencyId = this.isSysAdmin ?
      SessionStorageHelper.getNumberItem(SessionStorageItemKeys.AGENCY) :
      actor.agencyId === null ? undefined : actor.agencyId;
    this.partnershipModeOptions = this.isSysAdmin
      ? createSelectOptionsFromEnum(PartnershipMode, 'common.partnershipMode.')
      : [];
  }

  get state () {
    return {
      agencyId: this.agencyId,
      partnershipModeId: this.partnershipModeId
    };
  }

  setAgencyId = (agencyId?: number) => {
    if (this.agencyId === agencyId) {
      return;
    }
    this.agencyId = agencyId;
    this.updateState();
  }

  setPartnershipModeId = async (partnershipModeId?: PartnershipMode) => {
    if (this.partnershipModeId === partnershipModeId) {
      return;
    }
    const previousPartnershipModeId = this.partnershipModeId;
    this.partnershipModeId = partnershipModeId;
    this.updateState();
    const succeeded = await this.getAdvertiserListModel().reload();
    if (!succeeded) {
      // 請求失敗時表格仍是篩選前的資料，下拉選單要跟著回復，畫面才不會宣稱已套用。
      this.partnershipModeId = previousPartnershipModeId;
      this.updateState();
    }
  }

  setAgencyOptions (agencyOptions: SelectOptions[]): void {
    this.agencyOptions = agencyOptions;
    this.updateState();
  }

  getAdvertiserListModel (): AdvertiserListModel {
    if (this.advertiserListModel && this.advertiserListModel.agencyId === this.agencyId) {
      return this.advertiserListModel;
    }

    this.advertiserListModel = new DefaultAdvertiserListModel(
      this.agencyId,
      this.isSysAdmin,
      () => this.partnershipModeId
    );
    return this.advertiserListModel;
  }

  onUnmount (eventHandler: number | undefined = undefined): void {
    if (eventHandler !== undefined) {
      this.event.remove(eventHandler);
    }
  }

  updateState () {
    this.event.fireEvent(this);
  }
}
